import { normalise, colonneRecherche, proches } from '../search.ts'
import { fusionnerQuantites, normaliseQuantite } from '../quantite.ts'
import {
  type Contexte,
  type Lecture,
  type Ligne,
  type Mutation,
  type Op,
  type Resultat,
  applique,
  ajuste,
  rejete,
} from './types.ts'

export * from './types.ts'

/**
 * LES 17 OPÉRATIONS — le fichier le plus important du projet.
 *
 * On journalise des INTENTIONS, pas des lignes. Une intention porte ce que
 * la personne voulait (« mets le beurre à épuisé »), pas l'état de la ligne
 * après coup. Deux intentions concurrentes sur deux champs différents
 * s'appliquent donc toutes les deux, là où deux lignes complètes en
 * écraseraient une.
 *
 * CES FONCTIONS SONT PURES et servent des DEUX côtés : le client les applique
 * de façon optimiste sur son miroir, le serveur les applique pour de vrai.
 * C'est ce partage qui empêche la pire classe de bugs du hors-ligne — une
 * ligne qui apparaît puis disparaît parce que le client et le serveur
 * n'étaient pas d'accord sur ce que l'opération signifiait.
 *
 * Toute modification ici doit passer par le banc de rejeu (index.test.ts),
 * qui compare l'application des deux côtés sur des séquences entrelacées.
 */


type Gestionnaire = (op: Op, etat: Lecture, ctx: Contexte) => Resultat

/** Une suppression est ABSORBANTE : toute opération arrivant sur une ligne
 *  déjà supprimée est rejetée. Sans cette règle, une coche en retard
 *  ressusciterait un article effacé. */
function vivante(etat: Lecture, table: Parameters<Lecture['get']>[0], id: string): Ligne | undefined {
  const l = etat.get(table, id)
  if (!l || l.deletedAt) return undefined
  return l
}

const ETATS_STOCK = ['en_stock', 'bientot_fini', 'epuise'] as const
export type EtatStock = (typeof ETATS_STOCK)[number]

/* ===================================================================== */
/* Inventaire                                                            */
/* ===================================================================== */

/**
 * La ligne d'inventaire de ce produit à cet endroit — PIERRE TOMBALE
 * COMPRISE.
 *
 * Chercher uniquement parmi les lignes vivantes paraît naturel et produit
 * un bug de production : `inventory_unique (foyer, produit, lieu)` est un
 * index UNIQUE, et la suppression est une tombe — on n'efface jamais
 * physiquement. Une ligne supprimée continue donc d'occuper la place, et
 * en recréer une lève « UNIQUE constraint failed ». Exactement la même
 * collision que celle déjà payée sur les codes-barres du catalogue.
 *
 * On ressuscite donc la tombe au lieu de créer à côté. C'est aussi le bon
 * comportement métier : remettre du camembert au frigo après l'avoir
 * retiré, ce n'est pas un second camembert.
 */
function ligneInventaire(etat: Lecture, catalogItemId: string, locationId: string) {
  const lignes = etat
    .tous('inventoryItems')
    .filter((l) => l.catalogItemId === catalogItemId && l.locationId === locationId)
  return lignes.find((l) => !l.deletedAt) ?? lignes[0]
}

const itemCreer: Gestionnaire = (op, etat, ctx) => {
  const { id, catalogItemId, locationId, state } = op.payload as {
    id: string
    catalogItemId: string
    locationId: string
    state?: EtatStock
  }
  if (!id || !catalogItemId || !locationId) return rejete('payload', 'Création incomplète.')

  /*
   * FUSION par (produit, lieu). Deux personnes qui ajoutent « lait au frigo »
   * doivent obtenir UNE ligne, pas deux : un inventaire qui affiche le lait
   * en double est un inventaire auquel on cesse de faire confiance.
   * La seconde création devient donc une mise à jour d'état.
   */
  const existante = ligneInventaire(etat, catalogItemId, locationId)
  if (existante) {
    return applique([
      {
        type: 'modifier',
        table: 'inventoryItems',
        id: existante.id,
        champs: {
          state: state ?? 'en_stock',
          stateChangedAt: ctx.maintenant,
          // Résurrection si c'était une tombe. Voir `ligneInventaire`.
          deletedAt: null,
        },
      },
    ])
  }

  return applique([
    {
      type: 'creer',
      table: 'inventoryItems',
      ligne: {
        id,
        catalogItemId,
        locationId,
        state: state ?? 'en_stock',
        stateChangedAt: ctx.maintenant,
      },
    },
  ])
}

const itemEtat: Gestionnaire = (op, etat, ctx) => {
  const { id, state } = op.payload as { id: string; state: EtatStock }
  if (!ETATS_STOCK.includes(state)) return rejete('etat_inconnu', `État inconnu : ${state}`)

  const ligne = vivante(etat, 'inventoryItems', id)
  if (!ligne) return rejete('entity_deleted', "L'article n'existe plus.")

  /*
   * LE CONFLIT CENTRAL : « épuisé » décidé à la maison contre une validation
   * de courses faite en magasin.
   *
   * On arbitre par CAUSALITÉ, pas par horloge. Si l'appareil n'avait pas
   * encore vu la validation (op.baseRev < stateRev), son « épuisé » est une
   * observation sur un frigo qui n'était pas encore rempli : le sac gagne,
   * parce que c'est un fait physique. Si en revanche il avait vu la
   * validation et a quand même tapé, c'est une décision informée — par
   * exemple « il a acheté la mauvaise chose » — et elle s'applique.
   *
   * Une horloge ne saurait pas faire cette distinction : elle dirait
   * seulement qui a tapé en dernier.
   */
  const stateRev = Number(ligne.stateRev ?? 0)
  const posePar = String(ligne.stateSource ?? '')
  if (posePar === 'trip' && op.baseRev < stateRev && state === 'epuise') {
    return ajuste(
      'superseded_by_trip',
      'Racheté pendant les courses : le passage en « épuisé » n’a pas été retenu.',
    )
  }

  if (ligne.state === state) return applique([]) // idempotent, aucun bruit

  const mutations: Mutation[] = [
    {
      type: 'modifier',
      table: 'inventoryItems',
      id,
      champs: { state, stateChangedAt: ctx.maintenant, stateSource: 'manuel' },
    },
  ]

  /*
   * « ÉPUISÉ » N'AJOUTE PLUS RIEN À LA LISTE DE COURSES.
   *
   * Il le faisait, et c'était un raccourci défendable : on constate le
   * manque devant le frigo, pas plus tard devant une liste. L'usage a
   * tranché autrement — « il y en a que je n'achète que de temps en
   * temps ». Constater qu'on n'a plus de piment fumé n'est pas décider
   * d'en racheter, et la liste finissait par contenir des choses qu'on ne
   * voulait pas, qu'il fallait ensuite retirer une par une.
   *
   * Le manque est désormais une SUGGESTION, au même titre que « bientôt
   * fini » : l'article apparaît dans les propositions de l'écran Courses,
   * avec un + pour l'ajouter vraiment. Un geste de plus, mais seulement
   * pour ce qu'on veut réellement acheter — et c'est l'inverse qui coûtait
   * cher, puisque retirer demande autant de gestes qu'ajouter.
   *
   * `shoppingId` reste accepté dans la charge utile sans être lu : les
   * téléphones qui n'ont pas encore rechargé l'app continuent de l'envoyer.
   */

  return applique(mutations)
}

const itemDeplacer: Gestionnaire = (op, etat, ctx) => {
  const { id, locationId } = op.payload as { id: string; locationId: string }
  const ligne = vivante(etat, 'inventoryItems', id)
  if (!ligne) return rejete('entity_deleted', "L'article n'existe plus.")
  if (ligne.locationId === locationId) return applique([])

  /*
   * La destination peut DÉJÀ contenir ce produit — et `inventory_unique
   * (foyer, produit, lieu)` l'interdit en double. Déplacer le cumin du
   * frigo vers les épices alors qu'il y a déjà du cumin aux épices n'est
   * pas une erreur de l'utilisateur : c'est une fusion de deux lignes qui
   * décrivaient la même chose. On garde l'état le plus déplété, comme à la
   * fusion de deux produits — un déplacement ne doit jamais faire croire
   * qu'on possède ce qu'on n'a plus.
   */
  const occupante = etat
    .tous('inventoryItems')
    .find((l) => l.id !== id && l.catalogItemId === ligne.catalogItemId && l.locationId === locationId)

  if (!occupante) {
    return applique([{ type: 'modifier', table: 'inventoryItems', id, champs: { locationId } }])
  }

  const RANG: Record<string, number> = { en_stock: 0, bientot_fini: 1, epuise: 2 }
  const pire =
    occupante.deletedAt || (RANG[String(ligne.state)] ?? 0) > (RANG[String(occupante.state)] ?? 0)
      ? ligne.state
      : occupante.state

  return applique([
    {
      type: 'modifier',
      table: 'inventoryItems',
      id: String(occupante.id),
      champs: { state: pire, stateChangedAt: ctx.maintenant, deletedAt: null },
    },
    { type: 'supprimer', table: 'inventoryItems', id },
  ])
}

const itemNote: Gestionnaire = (op, etat) => {
  const { id, note } = op.payload as { id: string; note: string | null }
  if (!vivante(etat, 'inventoryItems', id)) return rejete('entity_deleted', "L'article n'existe plus.")
  // Dernier arrivé : une note est du texte libre, la fusionner n'aurait pas
  // de sens, et deux personnes qui annotent le même article au même moment
  // est un cas qui ne se produit pas.
  return applique([
    { type: 'modifier', table: 'inventoryItems', id, champs: { note: note || null } },
  ])
}

const itemSupprimer: Gestionnaire = (op, etat) => {
  const { id } = op.payload as { id: string }
  if (!vivante(etat, 'inventoryItems', id)) return applique([]) // déjà fait : absorbant
  return applique([{ type: 'supprimer', table: 'inventoryItems', id }])
}

/* ===================================================================== */
/* Péremptions                                                           */
/* ===================================================================== */

const expiryAjouter: Gestionnaire = (op, etat) => {
  const { id, inventoryItemId, date, label } = op.payload as {
    id: string
    inventoryItemId: string
    date: string
    label?: string
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return rejete('date', 'Date attendue au format AAAA-MM-JJ.')
  if (!vivante(etat, 'inventoryItems', inventoryItemId))
    return rejete('entity_deleted', "L'article n'existe plus.")
  // Un ensemble d'éléments identifiés est un CRDT gratuit : ajouts et
  // retraits par id ne se contredisent jamais.
  return applique([
    { type: 'creer', table: 'expiries', ligne: { id, inventoryItemId, date, label: label ?? null } },
  ])
}

const expiryRetirer: Gestionnaire = (op, etat) => {
  const { id } = op.payload as { id: string }
  if (!vivante(etat, 'expiries', id)) return applique([])
  return applique([{ type: 'supprimer', table: 'expiries', id }])
}

/* ===================================================================== */
/* Liste de courses                                                      */
/* ===================================================================== */

function ordreRayon(etat: Lecture, rayonId: unknown): number {
  if (!rayonId) return 999
  const r = etat.get('rayons', String(rayonId))
  return r ? Number(r.sortOrder ?? 999) : 999
}

const listeAjouter: Gestionnaire = (op, etat) => {
  const { id, catalogItemId, freeText, reason, sourceRecipeId, note, quantityText } = op.payload as {
    id: string
    catalogItemId?: string
    freeText?: string
    reason?: string
    sourceRecipeId?: string
    note?: string
    quantityText?: string
  }
  if (!catalogItemId && !freeText) return rejete('payload', 'Ni produit ni texte libre.')
  const quantite = normaliseQuantite(quantityText)

  /*
   * FUSION. Deux personnes ajoutent « beurre » à dix minutes d'écart : deux
   * lignes dans le rayon crèmerie, c'est une erreur d'achat. On fusionne sur
   * le produit du catalogue, et sur le texte NORMALISÉ pour le texte libre —
   * mais on ne devine pas que « PQ » et « papier toilette » sont la même
   * chose sans passer par le catalogue, et c'est assumé.
   */
  const cible = etat.tous('shoppingItems').find((s) => {
    if (s.deletedAt || s.tripId || s.state !== 'a_prendre') return false
    if (catalogItemId) return s.catalogItemId === catalogItemId
    return typeof s.freeText === 'string' && normalise(s.freeText) === normalise(freeText!)
  })

  if (cible) {
    // Déjà présent : on ne duplique pas, et on ne bruite pas non plus. Une
    // quantité, elle, s'AJOUTE à celle de la ligne (voir fusionnerQuantites).
    const champs: Record<string, unknown> = {}
    if (note) champs.note = note
    if (quantite) champs.quantityText = fusionnerQuantites(cible.quantityText, quantite)
    return applique(
      Object.keys(champs).length
        ? [{ type: 'modifier', table: 'shoppingItems', id: cible.id, champs }]
        : [],
    )
  }

  const produit = catalogItemId ? etat.get('catalogItems', catalogItemId) : undefined
  return applique([
    {
      type: 'creer',
      table: 'shoppingItems',
      ligne: {
        id,
        catalogItemId: catalogItemId ?? null,
        freeText: catalogItemId ? null : freeText,
        rayonId: produit?.rayonId ?? null,
        rayonSortOrder: ordreRayon(etat, produit?.rayonId),
        state: 'a_prendre',
        addedReason: reason ?? 'manuel',
        sourceRecipeId: sourceRecipeId ?? null,
        note: note ?? null,
        quantityText: quantite,
      },
    },
  ])
}

/**
 * REMPLACE la quantité d'une ligne — `list.add` ne sait qu'en ajouter.
 *
 * Sans ce geste, corriger « 250 g + 500 g » en « 500 g » obligerait à retirer
 * la ligne et à la recréer. Vide ou `null` efface la quantité. Deux appareils
 * qui corrigent en même temps : le dernier arrivé gagne, comme pour `note` —
 * ce n'est pas une donnée sur laquelle un arbitrage vaudrait son coût.
 */
const listeQuantite: Gestionnaire = (op, etat) => {
  const { id, quantityText } = op.payload as { id: string; quantityText?: string | null }
  const ligne = vivante(etat, 'shoppingItems', id)
  if (!ligne) return rejete('entity_deleted', "La ligne n'existe plus.")
  if (ligne.tripId) return ajuste('deja_valide', 'Cette ligne appartient à une sortie déjà validée.')
  return applique([
    {
      type: 'modifier',
      table: 'shoppingItems',
      id,
      champs: { quantityText: normaliseQuantite(quantityText) },
    },
  ])
}

const listeCocher: Gestionnaire = (op, etat, ctx) => {
  const { id, checked } = op.payload as { id: string; checked: boolean }
  const ligne = vivante(etat, 'shoppingItems', id)
  if (!ligne) return rejete('entity_deleted', "La ligne n'existe plus.")
  if (ligne.tripId) return ajuste('deja_valide', 'Cette ligne appartient à une sortie déjà validée.')

  /*
   * Cocher ne touche PAS l'inventaire. C'est ce qui fait que « A met épuisé
   * à la maison pendant que B coche en magasin » n'est pas un conflit du
   * tout : entités différentes, champs différents, les deux s'appliquent.
   * Le stock ne bouge qu'à la validation de la sortie.
   */
  return applique([
    {
      type: 'modifier',
      table: 'shoppingItems',
      id,
      champs: {
        state: checked ? 'pris' : 'a_prendre',
        checkedAt: checked ? ctx.maintenant : null,
        checkedBy: checked ? ctx.userId : null,
      },
    },
  ])
}

const listeRetirer: Gestionnaire = (op, etat) => {
  const { id } = op.payload as { id: string }
  if (!vivante(etat, 'shoppingItems', id)) return applique([])
  // La suppression gagne sur la coche, quel que soit l'ordre : retirer un
  // article est une décision, le cocher ensuite n'a pas de sens.
  return applique([{ type: 'supprimer', table: 'shoppingItems', id }])
}

/**
 * Validation du passage en caisse — l'opération PRIVILÉGIÉE.
 *
 * Elle encode un fait vérifié physiquement : le produit est dans le sac.
 * C'est pour ça qu'elle l'emporte sur un « épuisé » décidé depuis la
 * cuisine (voir itemEtat).
 */
const sortieValider: Gestionnaire = (op, etat, ctx) => {
  const { tripId, itemIds, storeName, nouveauxIds } = op.payload as {
    tripId: string
    itemIds: string[]
    storeName?: string
    /*
     * Identifiants pré-générés PAR LE CLIENT pour les lignes d'inventaire
     * à créer, indexés par ligne de courses.
     *
     * Le serveur ne peut pas les tirer lui-même : l'état affiché est
     * `fold(applyOp, lignesServeur, outbox)`, donc le client rejoue la
     * même opération de son côté. Un identifiant tiré au hasard dans le
     * gestionnaire donnerait deux lignes différentes des deux côtés. Même
     * discipline que `shoppingId` dans `item.setState`.
     */
    nouveauxIds?: Record<string, string>
  }
  if (!Array.isArray(itemIds)) return rejete('payload', 'Liste des articles manquante.')

  const mutations: Mutation[] = [
    {
      type: 'creer',
      table: 'trips',
      ligne: {
        id: tripId,
        validatedAt: ctx.maintenant,
        validatedBy: ctx.userId,
        storeName: storeName ?? null,
        itemCount: itemIds.length,
      },
    },
  ]

  for (const itemId of itemIds) {
    const ligne = vivante(etat, 'shoppingItems', itemId)
    if (!ligne) continue // supprimée entre-temps : la suppression est absorbante

    mutations.push({ type: 'modifier', table: 'shoppingItems', id: itemId, champs: { tripId } })

    // Faire les courses REMPLIT l'inventaire : c'est ce qui referme la boucle.
    if (!ligne.catalogItemId) continue
    const stock = etat
      .tous('inventoryItems')
      .find((i) => !i.deletedAt && i.catalogItemId === ligne.catalogItemId)

    if (stock) {
      mutations.push({
        type: 'modifier',
        table: 'inventoryItems',
        id: stock.id,
        champs: { state: 'en_stock', stateChangedAt: ctx.maintenant, stateSource: 'trip' },
      })
      continue
    }

    /*
     * RIEN EN STOCK : c'est un premier achat, et il faut CRÉER la ligne.
     *
     * Sans ça, la boucle ne se refermait que pour ce que le foyer possédait
     * déjà. On scanne un saucisson inconnu dans la liste, on l'achète, on
     * valide — et il n'apparaît nulle part. Il a bien été acheté, la ligne
     * de courses est rattachée à la sortie, mais l'inventaire l'ignore. Le
     * commentaire au-dessus affirmait que faire les courses remplit
     * l'inventaire ; il n'était vrai qu'à moitié.
     */
    const nouvelId = nouveauxIds?.[itemId]
    if (!nouvelId) continue

    const produit = vivante(etat, 'catalogItems', String(ligne.catalogItemId))
    const lieux = etat
      .tous('locations')
      .filter((l) => !l.deletedAt)
      .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0))
    // L'emplacement habituel du produit, sinon le premier — le frigo. Se
    // tromper d'étagère se corrige d'un geste ; ne pas apparaître du tout,
    // non.
    const lieu = produit?.defaultLocationId
      ? String(produit.defaultLocationId)
      : lieux[0] && String(lieux[0].id)
    if (!lieu) continue

    const tombe = ligneInventaire(etat, String(ligne.catalogItemId), lieu)
    mutations.push(
      tombe
        ? {
            type: 'modifier',
            table: 'inventoryItems',
            id: String(tombe.id),
            champs: {
              state: 'en_stock',
              stateChangedAt: ctx.maintenant,
              stateSource: 'trip',
              deletedAt: null,
            },
          }
        : {
            type: 'creer',
            table: 'inventoryItems',
            ligne: {
              id: nouvelId,
              catalogItemId: ligne.catalogItemId,
              locationId: lieu,
              state: 'en_stock',
              stateChangedAt: ctx.maintenant,
              stateSource: 'trip',
            },
          },
    )
  }

  return applique(mutations)
}

/* ===================================================================== */
/* Catalogue                                                             */
/* ===================================================================== */

const catalogueCreer: Gestionnaire = (op, etat) => {
  const p = op.payload as {
    id: string
    name: string
    aliases?: string[]
    rayonId?: string
    defaultLocationId?: string
    emoji?: string
    barcode?: string
  }
  if (!p.name?.trim()) return rejete('payload', 'Nom manquant.')

  /*
   * Majuscule initiale : tout le catalogue est capitalisé (« Crème fraîche
   * épaisse »), et un « crème anglaise » minuscule au milieu détonnerait
   * dans la liste. On ne touche PAS au reste de la casse — « NESQUIK » et
   * « pH neutre » doivent rester tels quels.
   */
  const nom = p.name.trim().replace(/^\p{Ll}/u, (c) => c.toUpperCase())
  const doublon = etat
    .tous('catalogItems')
    .find((c) => !c.deletedAt && normalise(String(c.name)) === normalise(nom))
  if (doublon) return applique([]) // idempotent

  /*
   * L'unicité du code-barres était portée par un index ; elle est
   * maintenant une règle métier, parce qu'elle porte sur le contenu d'un
   * tableau. Idempotent volontairement : deux scans du même paquet ne
   * doivent pas produire d'erreur, juste ne rien créer.
   */
  if (p.barcode) {
    const dejaScanne = etat
      .tous('catalogItems')
      .find((c) => !c.deletedAt && ((c.barcodes ?? []) as string[]).includes(p.barcode!))
    if (dejaScanne) return applique([])
  }

  /*
   * RAYON DEVINÉ à partir du produit connu le plus proche.
   *
   * Sans ça, tout ce qu'on crée à la main ou par un scan arrive sans
   * rayon : classé nulle part en mode magasin, et surtout affiché sans
   * emoji, puisque l'emoji retombe sur celui du rayon. Une ligne nue au
   * milieu des autres se lit comme un oubli alors que c'est la normale.
   *
   * On hérite du rayon ET de l'emplacement habituel, pas de l'emoji.
   *
   * L'emoji propre du voisin serait spécifique, donc visiblement faux —
   * celui du rayon suffit, et il est juste par construction.
   *
   * Pour l'emplacement j'avais tranché l'inverse : une devinette n'a pas à
   * décider où atterrissent les scans. C'était une erreur de raisonnement,
   * payée tout de suite — l'alternative n'était pas « aucune décision »,
   * c'était « tout au frigo », puisque l'écran d'ajout retombait sur le
   * premier emplacement. Un foyer s'est retrouvé avec toutes ses épices au
   * réfrigérateur. Entre deux devinettes, celle qui regarde le produit
   * voisin bat celle qui ne regarde rien.
   */
  let rayonId = p.rayonId ?? null
  let defaultLocationId = p.defaultLocationId ?? null
  if (!rayonId || !defaultLocationId) {
    const voisins = etat
      .tous('catalogItems')
      .filter((c) => !c.deletedAt && (c.rayonId || c.defaultLocationId))
      .map((c) => ({
        nom: String(c.name),
        search: String(c.search),
        tier: Number(c.tier ?? 1),
        rayonId: c.rayonId ? String(c.rayonId) : null,
        defaultLocationId: c.defaultLocationId ? String(c.defaultLocationId) : null,
      }))
    const voisin = proches(voisins, nom, 1)[0]
    rayonId = rayonId ?? voisin?.rayonId ?? null
    defaultLocationId = defaultLocationId ?? voisin?.defaultLocationId ?? null
  }

  return applique([
    {
      type: 'creer',
      table: 'catalogItems',
      ligne: {
        id: p.id,
        name: nom,
        search: colonneRecherche(nom, p.aliases ?? []),
        aliases: p.aliases ?? [],
        emoji: p.emoji ?? null,
        rayonId,
        defaultLocationId,
        barcodes: p.barcode ? [p.barcode] : [],
        origin: p.barcode ? 'barcode' : 'user',
        tier: 1,
      },
    },
  ])
}

const catalogueModifier: Gestionnaire = (op, etat) => {
  const { id, patch } = op.payload as { id: string; patch: Record<string, unknown> }
  const ligne = vivante(etat, 'catalogItems', id)
  if (!ligne) return rejete('entity_deleted', "Le produit n'existe plus.")

  /*
   * LWW PAR CHAMP PRÉSENT dans le patch, pas sur la ligne entière : Marie
   * corrige le rayon pendant que Tristan ajoute un alias, les deux doivent
   * tenir. Écraser la ligne entière en perdrait un.
   */
  const champs: Record<string, unknown> = { ...patch }
  if ('name' in patch || 'aliases' in patch) {
    const nom = String(patch.name ?? ligne.name)
    const alias = (patch.aliases ?? ligne.aliases ?? []) as string[]
    champs.search = colonneRecherche(nom, alias)
  }
  return applique([{ type: 'modifier', table: 'catalogItems', id, champs }])
}

/**
 * FUSION de deux produits du catalogue.
 *
 * Le scan crée des entrées très spécifiques — « Petits Pois Doux Très Fins
 * CARREFOUR » — à côté d'entrées génériques de la graine — « Petits pois ».
 * Ce sont bien deux lignes pour une même chose dans le frigo, et sans
 * fusion l'inventaire se dédouble à chaque passage en caisse.
 *
 * RIEN N'EST PERDU, et c'est ce qui distingue une fusion d'une suppression :
 *  - le nom absorbé devient un ALIAS du survivant, donc il reste trouvable ;
 *  - le code-barres migre, donc un prochain scan tombe sur le bon produit ;
 *  - l'inventaire, les courses et les ingrédients de recettes sont
 *    repointés.
 *
 * L'état de stock retenu est LE PLUS DÉPLÉTÉ des deux. Une fusion ne doit
 * jamais faire croire qu'on possède quelque chose qu'on n'a plus : entre
 * « en stock » et « épuisé », on garde « épuisé » et on le signale.
 */
const catalogueFusionner: Gestionnaire = (op, etat, ctx) => {
  const { garder, absorber } = op.payload as { garder: string; absorber: string }
  if (garder === absorber) return applique([])

  const survivant = vivante(etat, 'catalogItems', garder)
  const absorbe = vivante(etat, 'catalogItems', absorber)
  if (!survivant) return rejete('entity_deleted', 'Le produit à garder n’existe plus.')
  if (!absorbe) return applique([]) // déjà fusionné : idempotent

  const mutations: Mutation[] = []

  // 1. Le nom absorbé et ses alias rejoignent les alias du survivant.
  const aliasExistants = (survivant.aliases ?? []) as string[]
  const nouveaux = [String(absorbe.name), ...((absorbe.aliases ?? []) as string[])].filter(
    (a) => !aliasExistants.some((e) => normalise(e) === normalise(a)),
  )
  const aliases = [...aliasExistants, ...nouveaux]
  const champs: Record<string, unknown> = {
    aliases,
    search: colonneRecherche(String(survivant.name), aliases),
  }
  /*
   * 2. TOUS les codes-barres migrent, et la ligne absorbée doit être vidée
   * des siens.
   *
   * Vider n'est pas cosmétique. La suppression est une pierre tombale — on
   * n'efface jamais physiquement — donc sans ça la ligne morte continuerait
   * de revendiquer l'EAN, et le scan du paquet retomberait sur un produit
   * supprimé. C'est aussi ce qui faisait échouer la fusion du temps de
   * l'index unique.
   */
  const eansAbsorbes = (absorbe.barcodes ?? []) as string[]
  const eans = [...((survivant.barcodes ?? []) as string[])]
  for (const e of eansAbsorbes) if (!eans.includes(e)) eans.push(e)
  champs.barcodes = eans
  // L'ordre compte : on libère avant de reprendre.
  if (eansAbsorbes.length)
    mutations.push({
      type: 'modifier',
      table: 'catalogItems',
      id: absorber,
      champs: { barcodes: [] },
    })
  mutations.push({ type: 'modifier', table: 'catalogItems', id: garder, champs })

  // 3. L'inventaire.
  const RANG: Record<string, number> = { en_stock: 0, bientot_fini: 1, epuise: 2 }
  for (const ligne of etat.tous('inventoryItems')) {
    if (ligne.deletedAt || ligne.catalogItemId !== absorber) continue
    const cible = etat
      .tous('inventoryItems')
      .find(
        (l) =>
          !l.deletedAt && l.catalogItemId === garder && l.locationId === ligne.locationId,
      )
    if (cible) {
      // Les deux existent au même endroit : on garde l'état le plus déplété.
      const pire =
        (RANG[String(ligne.state)] ?? 0) > (RANG[String(cible.state)] ?? 0)
          ? ligne.state
          : cible.state
      mutations.push({
        type: 'modifier',
        table: 'inventoryItems',
        id: cible.id,
        champs: { state: pire, stateChangedAt: ctx.maintenant },
      })
      mutations.push({ type: 'supprimer', table: 'inventoryItems', id: ligne.id })
    } else {
      mutations.push({
        type: 'modifier',
        table: 'inventoryItems',
        id: ligne.id,
        champs: { catalogItemId: garder },
      })
    }
  }

  // 4. Les péremptions suivent leur ligne d'inventaire : rien à faire.

  // 5. La liste de courses.
  for (const ligne of etat.tous('shoppingItems')) {
    if (ligne.deletedAt || ligne.catalogItemId !== absorber) continue
    const doublon = etat
      .tous('shoppingItems')
      .find((l) => !l.deletedAt && !l.tripId && l.catalogItemId === garder && l.id !== ligne.id)
    if (doublon && !ligne.tripId) {
      // « Rien n'est perdu » vaut aussi pour la quantité de la ligne absorbée.
      const quantite = fusionnerQuantites(doublon.quantityText, ligne.quantityText)
      if (quantite !== (doublon.quantityText ?? null)) {
        mutations.push({
          type: 'modifier',
          table: 'shoppingItems',
          id: doublon.id,
          champs: { quantityText: quantite },
        })
      }
      mutations.push({ type: 'supprimer', table: 'shoppingItems', id: ligne.id })
    } else {
      mutations.push({
        type: 'modifier',
        table: 'shoppingItems',
        id: ligne.id,
        champs: { catalogItemId: garder },
      })
    }
  }

  // 6. Les ingrédients de recettes.
  for (const ing of etat.tous('recipeIngredients')) {
    if (ing.deletedAt || ing.catalogItemId !== absorber) continue
    mutations.push({
      type: 'modifier',
      table: 'recipeIngredients',
      id: ing.id,
      champs: { catalogItemId: garder },
    })
  }

  // 7. Et seulement maintenant, la ligne absorbée.
  mutations.push({ type: 'supprimer', table: 'catalogItems', id: absorber })

  return applique(mutations)
}

/* ===================================================================== */
/* Recettes                                                              */
/* ===================================================================== */

const recetteEnregistrer: Gestionnaire = (op, etat) => {
  const p = op.payload as {
    id: string
    title: string
    servings?: number
    prepMinutes?: number
    cookMinutes?: number
    steps?: string[]
    sourceUrl?: string
    tags?: string[]
    notes?: string
    ingredients?: Array<{
      id: string
      catalogItemId?: string
      freeText?: string
      quantityText?: string
      optional?: boolean
    }>
  }
  if (!p.title?.trim()) return rejete('payload', 'Titre manquant.')

  /*
   * LWW sur le DOCUMENT ENTIER, et non champ par champ comme le catalogue.
   * Fusionner une liste d'étapes ORDONNÉE produit des monstres — des étapes
   * dupliquées, dans le désordre, que personne ne peut suivre. L'édition
   * concurrente d'une recette est par ailleurs rarissime. Un remplacement
   * franc, dont l'UI dit « Marie a modifié cette recette pendant que tu
   * l'éditais », est plus honnête qu'une fusion approximative.
   */
  const existante = etat.get('recipes', p.id)
  const mutations: Mutation[] = [
    existante
      ? {
          type: 'modifier',
          table: 'recipes',
          id: p.id,
          champs: {
            title: p.title.trim(),
            servings: p.servings ?? null,
            prepMinutes: p.prepMinutes ?? null,
            cookMinutes: p.cookMinutes ?? null,
            steps: p.steps ?? [],
            sourceUrl: p.sourceUrl ?? null,
            tags: p.tags ?? [],
            notes: p.notes ?? null,
            deletedAt: null,
          },
        }
      : {
          type: 'creer',
          table: 'recipes',
          ligne: {
            id: p.id,
            title: p.title.trim(),
            servings: p.servings ?? null,
            prepMinutes: p.prepMinutes ?? null,
            cookMinutes: p.cookMinutes ?? null,
            steps: p.steps ?? [],
            sourceUrl: p.sourceUrl ?? null,
            tags: p.tags ?? [],
            notes: p.notes ?? null,
          },
        },
  ]

  // Les ingrédients font partie du document : on remplace l'ensemble.
  for (const anc of etat.tous('recipeIngredients')) {
    if (!anc.deletedAt && anc.recipeId === p.id) {
      mutations.push({ type: 'supprimer', table: 'recipeIngredients', id: anc.id })
    }
  }
  ;(p.ingredients ?? []).forEach((ing, i) => {
    mutations.push({
      type: 'creer',
      table: 'recipeIngredients',
      ligne: {
        id: ing.id,
        recipeId: p.id,
        catalogItemId: ing.catalogItemId ?? null,
        freeText: ing.freeText ?? null,
        quantityText: ing.quantityText ?? null,
        optional: ing.optional ?? false,
        sortOrder: i,
      },
    })
  })

  return applique(mutations)
}

const recetteSupprimer: Gestionnaire = (op, etat) => {
  const { id } = op.payload as { id: string }
  if (!vivante(etat, 'recipes', id)) return applique([])
  const mutations: Mutation[] = [{ type: 'supprimer', table: 'recipes', id }]
  for (const ing of etat.tous('recipeIngredients')) {
    if (!ing.deletedAt && ing.recipeId === id) {
      mutations.push({ type: 'supprimer', table: 'recipeIngredients', id: ing.id })
    }
  }
  return applique(mutations)
}

/* ===================================================================== */
/* Planning                                                              */
/* ===================================================================== */

const planPoser: Gestionnaire = (op, etat) => {
  const { id, date, slot, recipeId, freeText, diners } = op.payload as {
    id: string
    date: string
    slot: 'midi' | 'soir'
    recipeId?: string | null
    freeText?: string | null
    diners?: string[]
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return rejete('date', 'Date attendue au format AAAA-MM-JJ.')
  if (slot !== 'midi' && slot !== 'soir') return rejete('slot', 'Créneau inconnu.')

  // La case (date, créneau) est atomique par nature : dernier arrivé.
  const existante = etat
    .tous('mealPlanEntries')
    .find((e) => !e.deletedAt && e.date === date && e.slot === slot)

  if (!recipeId && !freeText) {
    return existante
      ? applique([{ type: 'supprimer', table: 'mealPlanEntries', id: existante.id }])
      : applique([])
  }

  const champs = {
    recipeId: recipeId ?? null,
    freeText: freeText ?? null,
    diners: diners ?? [],
  }
  return existante
    ? applique([{ type: 'modifier', table: 'mealPlanEntries', id: existante.id, champs }])
    : applique([{ type: 'creer', table: 'mealPlanEntries', ligne: { id, date, slot, ...champs } }])
}

/* ===================================================================== */
/* Personnes et goûts                                                    */
/* ===================================================================== */

const personneEnregistrer: Gestionnaire = (op, etat) => {
  const p = op.payload as { id: string; name: string; emoji?: string; userId?: string }
  if (!p.name?.trim()) return rejete('payload', 'Prénom manquant.')
  const existante = etat.get('people', p.id)
  const champs = { name: p.name.trim(), emoji: p.emoji ?? null, userId: p.userId ?? null }
  return existante
    ? applique([{ type: 'modifier', table: 'people', id: p.id, champs }])
    : applique([{ type: 'creer', table: 'people', ligne: { id: p.id, ...champs } }])
}

const gouEnregistrer: Gestionnaire = (op, etat) => {
  const p = op.payload as {
    id: string
    personId?: string | null
    kind: string
    severity?: string
    targetCatalogItemId?: string | null
    freeText?: string | null
    note?: string | null
  }
  if (!['allergie', 'regime', 'n_aime_pas', 'regle'].includes(p.kind))
    return rejete('kind', 'Type de préférence inconnu.')

  const champs = {
    personId: p.personId ?? null,
    kind: p.kind,
    severity: p.severity ?? (p.kind === 'allergie' ? 'interdit' : 'evite'),
    targetCatalogItemId: p.targetCatalogItemId ?? null,
    freeText: p.freeText ?? null,
    note: p.note ?? null,
  }
  return etat.get('preferences', p.id)
    ? applique([{ type: 'modifier', table: 'preferences', id: p.id, champs }])
    : applique([{ type: 'creer', table: 'preferences', ligne: { id: p.id, ...champs } }])
}

const gouSupprimer: Gestionnaire = (op, etat) => {
  const { id } = op.payload as { id: string }
  if (!vivante(etat, 'preferences', id)) return applique([])
  return applique([{ type: 'supprimer', table: 'preferences', id }])
}

/* ===================================================================== */
/* Le registre                                                           */
/* ===================================================================== */

export const OPERATIONS: Record<string, Gestionnaire> = {
  'item.create': itemCreer,
  'item.setState': itemEtat,
  'item.move': itemDeplacer,
  'item.setNote': itemNote,
  'item.delete': itemSupprimer,
  'expiry.add': expiryAjouter,
  'expiry.remove': expiryRetirer,
  'list.add': listeAjouter,
  'list.setQuantity': listeQuantite,
  'list.check': listeCocher,
  'list.remove': listeRetirer,
  'trip.validate': sortieValider,
  'catalog.create': catalogueCreer,
  'catalog.update': catalogueModifier,
  'catalog.merge': catalogueFusionner,
  'recipe.upsert': recetteEnregistrer,
  'recipe.delete': recetteSupprimer,
  'plan.set': planPoser,
  'person.upsert': personneEnregistrer,
  'pref.upsert': gouEnregistrer,
  'pref.delete': gouSupprimer,
}

export function appliquerOp(op: Op, etat: Lecture, ctx: Contexte): Resultat {
  const gestionnaire = OPERATIONS[op.kind]
  if (!gestionnaire) return rejete('kind_inconnu', `Opération inconnue : ${op.kind}`)
  try {
    return gestionnaire(op, etat, ctx)
  } catch (err) {
    // Une opération ne doit JAMAIS faire tomber une synchronisation entière :
    // les autres opérations du lot sont légitimes et doivent passer.
    return rejete('exception', err instanceof Error ? err.message : 'Erreur inattendue.')
  }
}
